import { emailPresentationKey, itemPresentationKey, peekWidthKey } from "@baton/core";
import { PEEK_DEFAULT_WIDTH, PEEK_MIN_WIDTH, clampPeekWidth, type Presentation } from "@baton/ui";
import { lazy, Suspense, useCallback, useEffect, useState } from "react";
import { Navigate, useLocation, useNavigate, useParams, useSearchParams } from "react-router";
import { itemQuery } from "../../app/queries.ts";
import { useRuntime } from "../../app/RuntimeProvider.tsx";
import { useUiStore } from "../../app/uiStore.ts";
import { chunks } from "../../routes.tsx";
import { useMediaQuery } from "../layout/AppShell.tsx";
import { useSprintOrigin } from "../../cycle/data/sprintContext.ts";
import { useTaskPath } from "../../item/paths.ts";
import { EMAIL_PARAM, EMAIL_TAB_PARAM, emailPagePath, emailPlaceOf, readerTabOf, withReaderTab } from "../../m365/emails/params.ts";

/**
 * FR-015-040, FR-015-110, `contracts/screens.md` § Side peek slot: any layout route with
 * `?peek={item}` renders the item detail in the item dialog, its tabs beside the task at up to
 * `--dialog-width-xl` by `--dialog-height-xl`, or in the right-docked panel, resizable between 380 and
 * 720 px with the width persisted. `?present=` names the presentation; a link that names none opens in
 * the one this browser last chose, the side peek until the person chooses another (2026-10-03, by the
 * owner's approval of the product review, "Task view default: side peek, not the full-screen modal";
 * the dialog had been the default since 2026-09-15), and either switches to the other or to the full
 * page. The full page is remembered as the other two are (2026-10-05, the owner's "the next time I
 * open the same kind of element, it should open in modal"): with it last chosen, an opening that names
 * no presentation goes straight to the task's page, as it does below 768 px. The title row names the task's project and identifier once (`item/header/ItemCrumbs.tsx`),
 * and opened from a task collection it steps to the previous and the next task in the collection's
 * order with `k` and `j`, the arrows and two buttons (`sequence.ts`, 2026-10-03). `Esc` closes it and returns focus to the originating row, and
 * below 768 px both become the full-page route. The frame of an open task (`ItemPeekFrame.tsx`) and
 * the detail module are lazy chunks (`research.md` W-26), prefetched on idle by the shell, so the
 * shell's first load pays for neither (FR-015-120, the frame since 2026-10-05). On a project's
 * routes `?activity=project` with no item open shows the project's recent activity in the same
 * side peek (`project/activity/ProjectActivityPeek.tsx`, 2026-09-17), at the peek's width, and on a
 * meeting's routes `?details=meeting` shows the meeting's Details in it (`meetings/page/MeetingDetails.tsx`,
 * 2026-10-02, the owner's "ensure the peak matches our task peak view"), loaded with the meeting page's
 * chunk, which those routes have loaded already (2026-10-21). On a client's page and the
 * Unassigned emails `?email={id}` with no task open reads that email in the same frame as a task
 * (`m365/emails/EmailPeek.tsx`, 022 FR-022-364 as amended 2026-10-07): the side peek docked beside the
 * page by default, the modal, or, chosen last or below 768 px, the email's full page. On a client's
 * `contacts/{contact}` route with no task open the contact's side peek stands over the client's page
 * (`accounts/contacts/ContactPeek.tsx`, 020 FR-020-290, 2026-10-15), closing back to the page.
 */
const ItemPeekFrame = lazy(() => chunks.peekFrame().then((module) => ({ default: module.ItemPeekFrame })));
const ProjectActivityPeek = lazy(() => import("../../project/activity/ProjectActivityPeek.tsx").then((module) => ({ default: module.ProjectActivityPeek })));
const MeetingDetailsPeek = lazy(() => chunks.meeting().then((module) => ({ default: module.MeetingDetailsPeek })));
const EmailPeek = lazy(() => import("../../m365/emails/EmailPeek.tsx").then((module) => ({ default: module.EmailPeek })));
const ContactPeek = lazy(() => import("../../accounts/contacts/ContactPeek.tsx").then((module) => ({ default: module.ContactPeek })));

export const PEEK_PARAM = "peek";
export const ACTIVITY_PARAM = "activity";
/** `?details=meeting` on a meeting's route: the meeting's Details in the side peek (018, 2026-10-02). */
export const DETAILS_PARAM = "details";
export const PRESENT_PARAM = "present";
export const COMMENT_PARAM = "comment";
/** The parameters of a task open over the page, which an email opened in the reader takes the place of (2026-10-07). */
export const TASK_PANEL_PARAMS: readonly string[] = [PEEK_PARAM, PRESENT_PARAM, COMMENT_PARAM];
const PANEL_QUERY = "(min-width: 768px)";

/** The search parameters of an open peek, kept beside the collection's own parameters; `preferred` is the presentation this browser last chose, which a link that names none opens in, the full page included (015 FR-015-040 as amended 2026-10-05). */
export function peekParams(search: URLSearchParams, preferred: Presentation): { readonly item: string | null; readonly presentation: Presentation; readonly comment: string | null } {
  const named = search.get(PRESENT_PARAM);
  return {
    item: search.get(PEEK_PARAM),
    presentation: named === "peek" || named === "modal" ? named : preferred,
    comment: search.get(COMMENT_PARAM),
  };
}

export function PeekSlot({ ws, narrow = false }: { readonly ws: string; readonly narrow?: boolean }) {
  const runtime = useRuntime();
  const navigate = useNavigate();
  const [search, setSearch] = useSearchParams();
  const { project: routeProject, meeting: routeMeeting, account: routeAccount, contact: routeContact } = useParams();
  const { pathname } = useLocation();
  const panelWide = useMediaQuery(PANEL_QUERY);
  const { item, presentation, comment } = peekParams(search, runtime.storage.get(itemPresentationKey) ?? "peek");
  const peekOrigin = useUiStore((state) => state.peekOrigin);
  // A peek over a sprint's page keeps the sprint's context when it becomes the task's page (2026-10-02).
  const sprintOrigin = useSprintOrigin(ws);
  // Opened over a client's pages, the task's page stays under the client, and elsewhere in the Clients area under the task's own client, which the open task's read names (2026-10-07).
  const taskPathOf = useTaskPath(ws);
  const pageOf = useCallback((ref: string) => taskPathOf(ref, runtime.queryClient.getQueryData(itemQuery(runtime, ws, ref).queryKey)?.account?.id ?? null), [taskPathOf, runtime, ws]);
  const setLastPeek = useUiStore((state) => state.setLastPeek);
  const [storedWidth, setWidth] = useState(() => runtime.storage.get(peekWidthKey) ?? PEEK_DEFAULT_WIDTH);
  // Both docks open on a page too narrow for them: the peek stands at its least width until one closes (019, `contracts/design-system.md` § Assistant panel).
  const width = narrow ? PEEK_MIN_WIDTH : storedWidth;

  useEffect(() => {
    if (item !== null) setLastPeek(item);
  }, [item, setLastPeek]);
  // A task opened over the page takes the place of an open email reader, in the address too, so closing the task does not bring the email back (2026-10-07).
  useEffect(() => {
    if (item === null || !search.has(EMAIL_PARAM)) return;
    const next = new URLSearchParams(search);
    next.delete(EMAIL_PARAM);
    next.delete(EMAIL_TAB_PARAM);
    setSearch(next, { replace: true });
  }, [item, search, setSearch]);

  /** The row that opened the peek, while it is still on the page. */
  const originRow = useCallback(() => (peekOrigin === null ? null : document.querySelector<HTMLElement>(`[data-item-id="${CSS.escape(peekOrigin)}"]`)), [peekOrigin]);
  const close = useCallback(() => {
    const next = new URLSearchParams(search);
    next.delete(PEEK_PARAM);
    next.delete(PRESENT_PARAM);
    next.delete(COMMENT_PARAM);
    setSearch(next, { replace: true });
    // The row that opened the side peek takes focus back (US3 scenario 1); the item dialog's focus
    // scope holds focus until the dialog has left, so the dialog returns it from `onCloseAutoFocus`.
    if (presentation === "peek") {
      queueMicrotask(() => {
        originRow()?.focus();
      });
    }
  }, [search, setSearch, presentation, originRow]);
  const onDialogCloseAutoFocus = useCallback(
    (event: Event) => {
      const row = originRow();
      if (row === null) return;
      event.preventDefault();
      row.focus();
    },
    [originRow],
  );

  const onWidthChange = useCallback(
    (next: number) => {
      const clamped = clampPeekWidth(next);
      setWidth(clamped);
      runtime.storage.set(peekWidthKey, clamped);
    },
    [runtime],
  );

  const changePresentation = useCallback(
    (next: Presentation) => {
      if (item === null) return;
      // The choice is remembered, so the next item opens the same way; the full page as well since 2026-10-05.
      runtime.storage.set(itemPresentationKey, next);
      if (next === "page") {
        // In place of the peek's entry: with the full page remembered, Back to that entry would only send the reader to the page again.
        void navigate(`${pageOf(item)}${comment === null ? "" : `?${COMMENT_PARAM}=${encodeURIComponent(comment)}`}`, { replace: true, state: sprintOrigin });
        return;
      }
      // Named in the URL as well, so this one stays as chosen.
      const params = new URLSearchParams(search);
      params.set(PRESENT_PARAM, next);
      setSearch(params, { replace: true });
    },
    [item, comment, navigate, pageOf, search, setSearch, runtime, sprintOrigin],
  );

  if (item === null) {
    const emailPlace = emailPlaceOf(ws, pathname);
    const email = emailPlace === null ? null : search.get(EMAIL_PARAM);
    if (emailPlace !== null && email !== null) {
      const emailPresentation = runtime.storage.get(emailPresentationKey) ?? "peek";
      if (!panelWide || emailPresentation === "page") {
        // As a task's: below the panel breakpoint, and wherever the full page was last chosen, the email is its page, in place of this entry.
        const tabbed = withReaderTab(new URLSearchParams(), readerTabOf(search)).toString();
        return <Navigate to={`${emailPagePath(ws, emailPlace, email)}${tabbed.length > 0 ? `?${tabbed}` : ""}`} replace />;
      }
      return (
        <Suspense fallback={null}>
          <EmailPeek ws={ws} place={emailPlace} emailId={email} opensIn={emailPresentation} width={width} onWidthChange={onWidthChange} />
        </Suspense>
      );
    }
    if (routeAccount !== undefined && routeContact !== undefined) {
      return (
        <Suspense fallback={null}>
          <ContactPeek
            ws={ws}
            accountId={routeAccount}
            contactId={routeContact}
            width={width}
            onWidthChange={onWidthChange}
            onClose={() => {
              void navigate({ pathname: `/${ws}/clients/${encodeURIComponent(routeAccount)}`, search: search.toString() }, { replace: true });
              // The contact's name in the client's Contacts panel, which opened the peek, takes focus back while it is still on the page.
              queueMicrotask(() => {
                document.querySelector<HTMLElement>(`[data-contact-id="${CSS.escape(routeContact)}"]`)?.focus();
              });
            }}
          />
        </Suspense>
      );
    }
    if (routeMeeting !== undefined && search.get(DETAILS_PARAM) === "meeting") {
      return (
        <Suspense fallback={null}>
          <MeetingDetailsPeek
            ws={ws}
            meetingId={routeMeeting}
            width={width}
            onWidthChange={onWidthChange}
            onClose={() => {
              const next = new URLSearchParams(search);
              next.delete(DETAILS_PARAM);
              setSearch(next, { replace: true });
            }}
          />
        </Suspense>
      );
    }
    if (routeProject === undefined || search.get(ACTIVITY_PARAM) !== "project") return null;
    return (
      <Suspense fallback={null}>
        <ProjectActivityPeek
          ws={ws}
          identifier={routeProject}
          width={width}
          onWidthChange={onWidthChange}
          onClose={() => {
            const next = new URLSearchParams(search);
            next.delete(ACTIVITY_PARAM);
            setSearch(next, { replace: true });
          }}
        />
      </Suspense>
    );
  }
  if (!panelWide || presentation === "page") {
    // Below the panel breakpoint the peek is the full-page route (FR-015-110), and so it is wherever the full page was last chosen (FR-015-040 as amended 2026-10-05).
    return <Navigate to={`${pageOf(item)}${comment === null ? "" : `?${COMMENT_PARAM}=${encodeURIComponent(comment)}`}`} state={sprintOrigin} replace />;
  }

  return (
    <Suspense fallback={null}>
      <ItemPeekFrame
        ws={ws}
        item={item}
        presentation={presentation}
        comment={comment}
        width={width}
        onWidthChange={onWidthChange}
        onClose={close}
        onPresentationChange={changePresentation}
        onDialogCloseAutoFocus={onDialogCloseAutoFocus}
      />
    </Suspense>
  );
}
